// @ts-check
// Navegação por teclado no canvas do bot (contrato de bots, seção 22.2), a mesma dos fluxos com os ramos de cada bloco
// (um por opção, mais "outra resposta" e "sem resposta"): direita para o próximo bloco (num bloco com ramos, o primeiro
// do primeiro ramo), esquerda para o anterior (do primeiro bloco de um ramo, o bloco dono), baixo e cima trocam de
// ramo, Home vai ao cartão de entrada e End ao último bloco do caminho. Ramo vazio tem foco no "+" dele. Pura.
import { FLOW_ENTRY_FOCUS, emptyListFocus, parseEmptyListFocus } from './flow-canvas-keyboard.js';
import { botBranchKeysOf, indexBotTree } from './bot-tree.js';

/**
 * @typedef {import('../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../shared/bots/bot-definition.js').BotNode} BotNode
 */

export const BOT_ENTRY_FOCUS = FLOW_ENTRY_FOCUS;

/** @param {BotNode} split @param {string} branchKey */
function firstOfBranch(split, branchKey) {
  const first = split.branches?.[branchKey]?.[0];
  return first ? first.id : emptyListFocus(split.id, branchKey);
}

/** @param {BotNode} split @param {string} branchKey @param {number} step */
function siblingBranch(split, branchKey, step) {
  const keys = botBranchKeysOf(split);
  const position = keys.indexOf(branchKey) + step;
  if (position < 0 || position >= keys.length) return null;
  return firstOfBranch(split, keys[position]);
}

/** @param {BotNode[]} list */
const lastOfList = (list) => list[list.length - 1]?.id ?? null;

/**
 * Próximo alvo do foco para a tecla, ou o mesmo quando não há para onde ir.
 * @param {BotDefinition} definition @param {string} focus
 * @param {'ArrowRight' | 'ArrowLeft' | 'ArrowDown' | 'ArrowUp' | 'Home' | 'End'} key
 * @returns {string}
 */
export function moveBotFocus(definition, focus, key) {
  if (key === 'Home') return BOT_ENTRY_FOCUS;
  const index = indexBotTree(definition);
  const rootFirst = definition.nodes[0]?.id ?? emptyListFocus(null, null);
  if (focus === BOT_ENTRY_FOCUS) {
    if (key === 'ArrowRight') return rootFirst;
    if (key === 'End') return lastOfList(definition.nodes) ?? BOT_ENTRY_FOCUS;
    return focus;
  }
  const empty = parseEmptyListFocus(focus);
  if (empty) {
    if (!empty.parentId) return key === 'ArrowLeft' ? BOT_ENTRY_FOCUS : focus;
    const split = index.get(empty.parentId)?.node;
    if (!split) return BOT_ENTRY_FOCUS;
    if (key === 'ArrowLeft') return split.id;
    if (key === 'ArrowDown' || key === 'ArrowUp') return siblingBranch(split, /** @type {string} */ (empty.branchKey), key === 'ArrowDown' ? 1 : -1) ?? focus;
    return focus;
  }
  const entry = index.get(focus);
  if (!entry) return BOT_ENTRY_FOCUS;
  const { node, siblings } = entry;
  const keys = botBranchKeysOf(node);
  switch (key) {
    case 'ArrowRight':
      if (keys.length > 0) return firstOfBranch(node, keys[0]);
      return siblings[entry.index + 1]?.id ?? focus;
    case 'ArrowLeft':
      if (entry.index > 0) return siblings[entry.index - 1].id;
      return entry.parentId ?? BOT_ENTRY_FOCUS;
    case 'ArrowDown':
    case 'ArrowUp': {
      const step = key === 'ArrowDown' ? 1 : -1;
      if (keys.length > 0) return firstOfBranch(node, keys[step > 0 ? keys.length - 1 : 0]);
      if (!entry.parentId || !entry.branchKey) return focus;
      const split = index.get(entry.parentId)?.node;
      return split ? siblingBranch(split, entry.branchKey, step) ?? focus : focus;
    }
    case 'End':
      return lastOfList(siblings) ?? focus;
    default:
      return focus;
  }
}
